/** Your top emoji: #5 to #2 slide in, a "?" wobbles, then #1 slams down and rains. */

import { useRef } from "react";

import { Scene, Zone, useStageHeight, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { wr } from "../../theme";
import type { You } from "../../types";
import { count } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "orange", x: 1010, y: 300, r: 160, seed: 7.3, wob: 0.2 },
  { colour: "fuchsia", x: 40, y: 760, r: 120, seed: 8.2, wob: 0.24 },
];

/** The video's seeded random, so the rain looks the same every time. */
function seeded(start: number) {
  let seed = start;
  return () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;
}

const rainSize = seeded(91);
const DROPS = Array.from({ length: 42 }, () => Math.round(70 + rainSize() * 90));

const times = (n: number) => `${count(n)} ${n === 1 ? "time" : "times"}`;

export function Emoji({ emoji }: { emoji: You["emoji"] }) {
  const shown = emoji.slice(0, 5);
  const [top, ...rest] = shown;
  // The timeline is built before the stage has measured the screen, so each drop reads the floor as it starts to fall.
  const tall = useStageHeight();
  const floor = useRef(tall);
  floor.current = tall;

  const root = useTimeline((s) => {
    s.rise(".kick", 0.35, { y: 0 });
    // Counting down: #5 first, #2 last.
    const rows = s.q(".erow").reverse();
    rows.forEach((row, k) => {
      s.tl.fromTo(row, { x: 1100, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.5, ease: "power4.out" }, 0.5 + k * 0.4);
      // Each row flies in from the right.
      s.sound("whoosh", 0.5 + k * 0.4, 1 + k * 0.1, { pan: 0.6, length: 0.4 });
    });
    // The reveal follows the last row, so a short list doesn't leave a gap.
    const q = 0.55 + rows.length * 0.4;
    s.pop(".e1q", q, { from: 0, dur: 0.5 });
    s.tl.to(".e1q", { scale: 1.08, rotation: 6, duration: 0.2, yoyo: true, repeat: 3, ease: "sine.inOut" }, q + 0.4);
    s.tl.to(".e1q", { scale: 0, autoAlpha: 0, duration: 0.15, ease: "power2.in" }, q + 1.25);
    s.slam(".e-one", q + 1.15);
    s.shake(".scene > .layer", q + 1.45, 10, 26);
    s.rise(".e-label", q + 1.65, { y: 30 });
    s.q(".e-drop").forEach((drop, i) => {
      const at = q + 1.45 + i * 0.03;
      const x = s.random() * 1020 - 40;
      const dur = 1.4 + s.random() * 0.9;
      s.tl.fromTo(
        drop,
        { x, y: -220, rotation: (s.random() - 0.5) * 90 },
        { x, y: () => floor.current + 180, rotation: (s.random() - 0.5) * 360, duration: dur, ease: "power1.in", immediateRender: false },
        at,
      );
      // A patter as the drops cross the middle, placed left to right where they fall.
      if (i % 3 === 0) s.sound("drip", at + dur * 0.55, 0.8 + s.random() * 0.8, { pan: (x / 1080) * 1.6 - 0.8 });
      // Shown only while falling, so none wait above or below the stage on a tall screen.
      s.tl.set(drop, { autoAlpha: 1 }, at);
      s.tl.set(drop, { autoAlpha: 0 }, at + dur);
    });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        {/* The rain falls the whole height of the stage, so it stays out of the zones. */}
        <div className="layer" aria-hidden>
          {DROPS.map((size, i) => (
            <div key={i} className="abs emo wr-hide e-drop" style={{ left: 0, top: 0, fontSize: size }}>
              {top.emoji}
            </div>
          ))}
        </div>
        <Zone at="top">
          <div className="kick">{shown.length === 1 ? "Your top emoji" : `Your top ${shown.length} emoji`}</div>
        </Zone>
        <Zone at="middle">
          <div className="e1q wr-hide" aria-hidden>
            ?
          </div>
          <div className="abs emo e-one" style={{ left: 0, right: 0, top: 420, textAlign: "center", fontSize: 360 }}>
            {top.emoji}
          </div>
          <div className="abs e-label" style={{ left: 0, right: 0, top: 830, textAlign: "center" }}>
            <div className="mono" style={{ fontSize: 36, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              #1 most used
            </div>
            <div className="disp" style={{ marginTop: 10, fontSize: 120 }}>
              {times(top.count)}
            </div>
          </div>
        </Zone>
        {/* The countdown is a list, so it keeps to the foot and the reveal keeps the middle. */}
        <Zone at="bottom">
          {rest.map((e, i) => (
            <div key={e.emoji} className="erow" style={{ top: 1070 + i * 152, color: wr("white") }}>
              <span className="rk">#{i + 2}</span>
              <span className="emo">{e.emoji}</span>
              <span className="n">{count(e.count)}</span>
              <span className="u">{e.count === 1 ? "time" : "times"}</span>
            </div>
          ))}
        </Zone>
      </Scene>
    </div>
  );
}
